import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardMedia,
  Stack,
  
  Avatar,
  Fade,
  Zoom,
  Slide,
  Tooltip,
  useTheme,
  alpha,
  Container,
  
  Backdrop,
  Grow,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  sendMessage,
  addListener,
  removeListener,
} from "../services/websocketManager";
import { LottieAnimation } from "../lottie/LottieAnimation";
import MovingText from "react-moving-text";
import {
  Replay as ReplayIcon,
  Home as HomeIcon,
  FilterNone as FilterNoneIcon,
  AutoGraph as AutoGraphIcon,
  EmojiEvents as TrophyIcon,
  Stars as StarsIcon,
  TrendingUp as TrendingUpIcon,
 
  Close as CloseIcon,
  Celebration as CelebrationIcon,
 
  FlashOn as FlashIcon,
  LocalFireDepartment as FlameIcon,
} from "@mui/icons-material";
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const GameEndInfo = ({
  gameInfoFromServer,
  onRematch,
  onPlaySameGame,
  onExitToMainMenu,
  onSaveGame,
  onShareGameHistory,
  onShareBoardScreenshot,
  onClose,
  isViewer,
  pgn,
  onAnalytics
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  const [imagePath, setImagePath] = useState(null);
  const [gameInfo, setGameInfo] = useState(null);
  const [generalStatus, setGeneralStatus] = useState(null);
  const [color, setColor] = useState("#000000");
  const [coins, setCoins] = useState(
    "https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743418251/Lottie/coinsMinus.json"
  );
  const [rematchRequested, setRematchRequested] = useState(false);
  const [rematchDialogOpen, setRematchDialogOpen] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [showContent, setShowContent] = useState(false);
  const [particles, setParticles] = useState([]);
  const [coinBounce, setCoinBounce] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [, setHoveredButton] = useState(null);
  const [showMovingText, setShowMovingText] = useState(true);
  const timeoutRef = useRef(null);
 
  const movingTextRef = useRef(null);
  const isMobile = window.innerWidth <= 600;

  // MovingText animation with 5-second pause
  useEffect(() => {
    if (generalStatus) {
      const startCycle = () => {
        setShowMovingText(true);
        
        // Hide after animation completes (approximately 3 seconds)
        setTimeout(() => {
          setShowMovingText(false);
        }, 3000);
        
        // Show again after 5 second pause
        setTimeout(() => {
          startCycle();
        }, 18000); // 3 seconds animation + 5 seconds pause
      };
      
      startCycle();
    }
  }, [generalStatus]);

  // Particle system for celebrations
  useEffect(() => {
    if (gameInfo?.gameGeneralStatus === "win") {
      setShowFireworks(true);
      const newParticles = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 6 + 2,
        color: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'][Math.floor(Math.random() * 5)],
        delay: Math.random() * 2,
        duration: Math.random() * 3 + 2,
      }));
      setParticles(newParticles);
    }
  }, [gameInfo?.gameGeneralStatus]);

  // Coin bounce effect - only once
  useEffect(() => {
    if (gameInfo?.coinsWinLoseDraw !== undefined) {
      const timer = setTimeout(() => {
        setCoinBounce(true);
        setTimeout(() => setCoinBounce(false), 600);
      }, 2000); // Trigger once after 2 seconds
      return () => clearTimeout(timer);
    }
  }, [gameInfo?.coinsWinLoseDraw]);

  // Animation entrance effect
  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const startTimer = () => {
    timeoutRef.current = setTimeout(() => {
      setResponseMessage(t("No response received. Rematch was rejected"));
      Swal.fire({
        title: t("Error"),
        text: t("No response received. Rematch was rejected"),
        icon: "error",
        confirmButtonText: t("OK"),
      });
      setRematchRequested(false);
    }, 10000);
  };

  useEffect(() => {
    const handleWebSocketEvent = (event) => {
      const message = event.message;
      switch (message.messageType) {
        case "rematch_request":
          setRematchDialogOpen(true);
          clearTimeout(timeoutRef.current);
          break;
        case "rematch_accept":
          clearTimeout(timeoutRef.current);
          Swal.fire({
            text: t("Rematch Accepted! Starting a new game..."),
            icon: "success",
            confirmButtonText: t("OK"),
          });
          setRematchDialogOpen(false);
          onRematch();
          break;
        case "rematch_reject":
          clearTimeout(timeoutRef.current);
          Swal.fire({
            text: t("Rematch Rejected!"),
            icon: "error",
            confirmButtonText: t("OK"),
          });
          setRematchRequested(false);
          break;
        default:
          break;
      }
    };
    addListener(handleWebSocketEvent);
    return () => {
      removeListener(handleWebSocketEvent);
      clearTimeout(timeoutRef.current);
    };
  }, [rematchRequested, gameInfoFromServer, t, onRematch]);

  useEffect(() => {
    if (gameInfoFromServer) {
      setGameInfo(gameInfoFromServer);
      const { gameGeneralStatus, pointsWinLoseDraw, gameStatus } =
        gameInfoFromServer;
      if (gameGeneralStatus === "win") {
        setGeneralStatus(t("Victory!"));
        setColor("#FFD700");
        setCoins(
          "https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743418251/Lottie/coinsPlus.json"
        );
      } else if (gameGeneralStatus === "defeat") {
        setGeneralStatus(t("Defeat"));
        setColor("#FF4444");
      } else if (gameGeneralStatus === "draw") {
        setGeneralStatus(t("draw_1"));
        setColor("#4ECDC4");
        setImagePath(
          "https://res.cloudinary.com/dnlgtek2a/image/upload/v1743426965/gameEnd/draw.webp"
        );
      }

      if (pointsWinLoseDraw < 0) {
        setColor("#FF4444");
        setCoins(
          "https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743418251/Lottie/coinsMinus.json"
        );
      }

      const imageMap = {
        white_won_by_mat_black: "white_won",
        white_won_by_timeout_black: "white_won_timeout",
        black_won_by_mat_white: "black_won",
        black_won_by_timeout_white: "black_won_by_timeout_white",
        white_won_by_surrendered_black: "white_won_surrender_black",
        black_won_by_surrendered_white: "black_won_by_surrendered_white",
      };
      const imageKey = imageMap[gameStatus];
      if (imageKey) {
        setImagePath(
          `https://res.cloudinary.com/dnlgtek2a/image/upload/v1743422158/gameEnd/${imageKey}.webp`
        );
      }
    }
  }, [gameInfoFromServer, t, theme]);

  const handleRematchClick = () => {
    setRematchRequested(true);
    sendMessage({
      messageType: "rematch_request",
      gameInfo: { gameId: gameInfoFromServer.gameId },
    });
    startTimer();
  };

  const handleAcceptRematch = () => {
    sendMessage({
      messageType: "rematch_accept",
      gameInfo: { gameId: gameInfoFromServer.gameId },
    });
    setRematchDialogOpen(false);
  };

  const handleRejectRematch = () => {
    sendMessage({
      messageType: "rematch_reject",
      gameInfo: { gameId: gameInfoFromServer.gameId },
    });
    setRematchDialogOpen(false);
  };

  const getStatusIcon = () => {
    if (gameInfo?.gameGeneralStatus === "win") return <TrophyIcon sx={{ fontSize: "3rem", filter: "drop-shadow(0 0 10px #FFD700)" }} />;
    if (gameInfo?.gameGeneralStatus === "draw") return <StarsIcon sx={{ fontSize: "3rem", filter: "drop-shadow(0 0 10px #4ECDC4)" }} />;
    return <TrendingDownIcon sx={{ fontSize: "3rem", filter: "drop-shadow(0 0 10px #FF4444)" }} />;
  };


  const InteractiveButton = ({ 
    onPress, 
    text, 
    icon, 
    color = "primary", 
    disabled = false, 
    variant = "contained",
    glowColor = "#4ECDC4"
  }) => (
    <Zoom in={showContent} style={{ transitionDelay: showContent ? '400ms' : '0ms' }}>
      <Button
        onClick={onPress}
        disabled={disabled}
        variant={variant}
        color={color}
        onMouseEnter={() => setHoveredButton(text)}
        onMouseLeave={() => setHoveredButton(null)}
        sx={{
          minWidth: isMobile ? "180px" : "250px",
          height: isMobile ? "48px" : "56px",
          borderRadius: "28px",
          fontSize: isMobile ? "0.9rem" : "1.1rem",
          fontWeight: 700,
          textTransform: "none",
          background: `linear-gradient(45deg, ${glowColor}22, ${glowColor}44)`,
          border: `2px solid ${glowColor}`,
          backdropFilter: "blur(20px)",
          boxShadow: `0 8px 32px ${alpha(glowColor, 0.3)}`,
          transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: "-100%",
            width: "100%",
            height: "100%",
            background: `linear-gradient(90deg, transparent, ${alpha(glowColor, 0.4)}, transparent)`,
            transition: "left 0.6s",
          },
          "&:hover": {
            transform: "translateY(-6px) scale(1.05)",
            boxShadow: `0 15px 30px ${alpha(glowColor, 0.4)}`,
            background: `linear-gradient(45deg, ${glowColor}44, ${glowColor}66)`,
          },
          "&:active": {
            transform: "translateY(-3px) scale(1.02)",
          },
          "&:disabled": {
            opacity: 0.6,
            transform: "none",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, zIndex: 1 }}>
          {icon}
          {text}
        </Box>
      </Button>
    </Zoom>
  );

  const ParticleSystem = () => (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 1,
      }}
    >
      {particles.map((particle) => (
        <Box
          key={particle.id}
          sx={{
            position: "absolute",
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            backgroundColor: particle.color,
            borderRadius: "50%",
            animation: `particleFloat ${particle.duration}s ${particle.delay}s infinite ease-in-out`,
            "@keyframes particleFloat": {
              "0%": {
                transform: "translateY(0px) rotate(0deg)",
                opacity: 1,
              },
              "50%": {
                transform: "translateY(-20px) rotate(180deg)",
                opacity: 0.8,
              },
              "100%": {
                transform: "translateY(-40px) rotate(360deg)",
                opacity: 0,
              },
            },
          }}
        />
      ))}
    </Box>
  );

  return (
    <>
      <Backdrop
        open={true}
        sx={{
          background: `radial-gradient(circle at center, ${alpha("#000", 0.8)}, ${alpha("#000", 0.95)})`,
          backdropFilter: "blur(10px)",
          zIndex: 1000,
        }}
      >
        <Container maxWidth="xl" sx={{ 
          display: "flex", 
          justifyContent: "center", 
          p: isMobile ? 1 : 2,
          height: "100vh",
          alignItems: "center"
        }}>
          <Grow in={true} timeout={1000}>
            <Card
              elevation={24}
              sx={{
                width: "100%",
                maxWidth: isMobile ? "95vw" : "900px",
                height: isMobile ? "100vh" : "90vh", // Reduced from 90vh to 64vh (25% reduction)
                borderRadius: isMobile ? "20px" : "32px",
                background: `linear-gradient(145deg, 
                  ${alpha("#000", 0.9)} 0%, 
                  ${alpha("#111", 0.95)} 25%,
                  ${alpha("#000", 0.9)} 50%,
                  ${alpha("#111", 0.95)} 75%,
                  ${alpha("#000", 0.9)} 100%)`,
                backdropFilter: "blur(40px)",
                border: `3px solid ${alpha(color, 0.5)}`,
                position: "relative",
                overflow: "hidden",
                boxShadow: `0 0 100px ${alpha(color, 0.3)}`,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "6px",
                  background: `linear-gradient(90deg, 
                    ${color} 0%, 
                    ${alpha(color, 0.8)} 25%,
                    ${alpha(color, 0.6)} 50%,
                    ${alpha(color, 0.8)} 75%,
                    ${color} 100%)`,
                  animation: "borderGlow 3s ease-in-out infinite",
                },
                "@keyframes borderGlow": {
                  "0%, 100%": { opacity: 0.8 },
                  "50%": { opacity: 1 },
                },
              }}
            >
              {/* Particle System */}
              {showFireworks && <ParticleSystem />}

              {/* Animated Background Effects */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: `radial-gradient(circle at 30% 20%, ${alpha(color, 0.1)} 0%, transparent 50%),
                              radial-gradient(circle at 70% 80%, ${alpha(color, 0.1)} 0%, transparent 50%)`,
                  animation: "backgroundPulse 4s ease-in-out infinite",
                  "@keyframes backgroundPulse": {
                    "0%, 100%": { opacity: 0.3 },
                    "50%": { opacity: 0.6 },
                  },
                }}
              />

              {/* Close Button with Effects */}
              <Tooltip title={t("Close")} arrow>
                <IconButton
                  onClick={onClose}
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    zIndex: 10,
                    width: 48,
                    height: 48,
                    background: `linear-gradient(45deg, ${alpha("#FF4444", 0.2)}, ${alpha("#FF4444", 0.4)})`,
                    backdropFilter: "blur(20px)",
                    border: "2px solid #FF4444",
                    color: "#FF4444",
                    "&:hover": {
                      transform: "scale(1.1) rotate(90deg)",
                      boxShadow: `0 0 30px ${alpha("#FF4444", 0.6)}`,
                      background: `linear-gradient(45deg, ${alpha("#FF4444", 0.4)}, ${alpha("#FF4444", 0.6)})`,
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <CloseIcon sx={{ fontSize: "1.25rem" }} />
                </IconButton>
              </Tooltip>

              {/* Main Content */}
              <CardContent sx={{ 
                p: isMobile ? 2 : 4, 
                display: "flex", 
                flexDirection: "column", 
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                position: "relative",
                zIndex: 2,
              }}>

                {/* Status Section with 3D Effect */}
                <Slide direction="down" in={showContent} timeout={1200}>
                  <Box sx={{ textAlign: "center", mb: isMobile ? 1 : 2 }}>
                    <Box
                      sx={{
                        position: "relative",
                        display: "inline-block",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "150px",
                          height: "150px",
                          background: `radial-gradient(circle, ${alpha(color, 0.3)}, transparent)`,
                          borderRadius: "50%",
                          animation: "statusGlow 2s ease-in-out infinite",
                        },
                        "@keyframes statusGlow": {
                          "0%, 100%": { transform: "translate(-50%, -50%) scale(1)" },
                          "50%": { transform: "translate(-50%, -50%) scale(1.2)" },
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: isMobile ? 60 : 100,
                          height: isMobile ? 60 : 100,
                          bgcolor: "transparent",
                          mb: isMobile ? 1 : 2,
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        {getStatusIcon()}
                      </Avatar>
                    </Box>

                    <Typography
                      variant="h5"
                      sx={{
                        color: color,
                        fontWeight: 900,
                        fontSize: isMobile ? "2.5rem" : "3.5rem",
                        textShadow: `0 0 30px ${alpha(color, 0.8)}`,
                        background: `linear-gradient(45deg, ${color}, ${alpha(color, 0.7)})`,
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        mb: 1,
                        animation: "textGlow 3s ease-in-out infinite",
                        "@keyframes textGlow": {
                          "0%, 100%": { filter: `drop-shadow(0 0 10px ${alpha(color, 0.5)})` },
                          "50%": { filter: `drop-shadow(0 0 30px ${alpha(color, 0.8)})` },
                        },
                        height: isMobile ? "60px" : "80px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      {showMovingText && (
                        <MovingText
                          ref={movingTextRef}
                          iteration={1}
                          type="typewriter"
                          delay={10}
                          dataText={[generalStatus || "Game Over"]}
                        />
                      )}
                      {!showMovingText && (generalStatus || "Game Over")}
                    </Typography>

                    {gameInfo?.gameGeneralStatus === "win" && (
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 1 }}>
                        <FlameIcon sx={{ color: "#FF6B35", fontSize: "1.5rem", animation: "flame 1s ease-in-out infinite" }} />
                        <CelebrationIcon sx={{ color: "#FFD700", fontSize: "1.5rem", animation: "celebrate 1.5s ease-in-out infinite" }} />
                        <FlashIcon sx={{ color: "#4ECDC4", fontSize: "1.5rem", animation: "flash 0.8s ease-in-out infinite" }} />
                      </Box>
                    )}
                  </Box>
                </Slide>

                {/* Game Result Image with 3D Hover */}
                {imagePath && (
                  <Zoom in={showContent} timeout={1500}>
                    <Box
                      sx={{
                        mb: 2,
                        perspective: "1000px",
                        "&:hover img": {
                          transform: "rotateY(10deg) rotateX(5deg) scale(1.05)",
                        },
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={imagePath}
                        alt="Game result"
                        sx={{
                          maxHeight: isMobile ? "120px" : "180px",
                          maxWidth: "100%",
                          objectFit: "contain",
                          borderRadius: "16px",
                          boxShadow: `0 15px 45px ${alpha(color, 0.3)}`,
                          transition: "transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                          filter: "brightness(1.1) contrast(1.1)",
                        }}
                      />
                    </Box>
                  </Zoom>
                )}

                {/* Interactive Coins Section */}
                {!isViewer && (
                  <Slide direction="up" in={showContent} timeout={1800}>
                    <Box sx={{ mb: 3, textAlign: "center" }}>
                      <Card
                        elevation={12}
                        sx={{
                          p: 2,
                          borderRadius: "20px",
                          background: `linear-gradient(145deg, 
                            ${alpha("#FFD700", 0.1)}, 
                            ${alpha("#FF8C00", 0.15)})`,
                          backdropFilter: "blur(20px)",
                          border: "2px solid #FFD700",
                          position: "relative",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: `linear-gradient(45deg, transparent, ${alpha("#FFD700", 0.1)}, transparent)`,
                            animation: "coinShimmer 3s ease-in-out infinite",
                          },
                          "@keyframes coinShimmer": {
                            "0%": { transform: "translateX(-100%)" },
                            "100%": { transform: "translateX(100%)" },
                          },
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            color: "#FFD700",
                            fontWeight: 700,
                            mb: 1,
                            textShadow: "0 0 20px rgba(255, 215, 0, 0.5)",
                            fontSize: isMobile ? "1.2rem" : "1.5rem"
                          }}
                        >
                          {t("Earned coins")}
                        </Typography>

                        <Box
                          sx={{
                            width: isMobile ? 80 : 100,
                            height: isMobile ? 80 : 100,
                            borderRadius: "50%",
                            background: `conic-gradient(from 0deg, #FFD700, #FF8C00, #FFD700)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto",
                            position: "relative",
                            animation: coinBounce ? "coinBounce 0.6s ease" : "none",
                            cursor: "pointer",
                            "&:hover": {
                              transform: "scale(1.1)",
                              boxShadow: "0 0 50px rgba(255, 215, 0, 0.8)",
                            },
                            "@keyframes coinBounce": {
                              "0%, 100%": { transform: "scale(1) rotateY(0deg)" },
                              "50%": { transform: "scale(1.2) rotateY(180deg)" },
                            },
                          }}
                          onClick={() => setCoinBounce(true)}
                        >
                          <Typography
                            variant="h2"
                            sx={{
                              color: "#000",
                              fontWeight: 900,
                              fontSize: isMobile ? "1.8rem" : "2.5rem",
                              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                            }}
                          >
                            {gameInfo?.coinsWinLoseDraw || 0}
                          </Typography>
                        </Box>
                      </Card>
                    </Box>
                  </Slide>
                )}

                {/* Interactive Action Buttons */}
                <Fade in={showContent} timeout={2000}>
                  <Stack spacing={2} alignItems="center" sx={{ width: "100%" }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      {localStorage.getItem("lastOpponentHumanOrAI") !== "AI" && (
                        <InteractiveButton
                          onPress={handleRematchClick}
                          text={rematchRequested ? t("Rematch Requested") : t("Rematch")}
                          icon={<ReplayIcon />}
                          disabled={rematchRequested}
                          glowColor="#4ECDC4"
                        />
                      )}
                      <InteractiveButton
                        onPress={onPlaySameGame}
                        text={t("Play_Same_Game")}
                        icon={<FilterNoneIcon />}
                        glowColor="#FF6B6B"
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      <InteractiveButton
                        onPress={onExitToMainMenu}
                        text={t("Exit_to_Main_Menu")}
                        icon={<HomeIcon />}
                        glowColor="#45B7D1"
                      />
                      
                      {gameInfo?.gameId && pgn && (
                        <InteractiveButton
                          onPress={onAnalytics}
                          text={t("Analyze Game")}
                          icon={<AutoGraphIcon />}
                          glowColor="#96CEB4"
                        />
                      )}
                    </Box>
                  </Stack>
                </Fade>

                {/* Lottie Animation with Enhanced Positioning */}
                {gameInfo?.pointsWinLoseDraw >= 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: -10,
                      left: "50%",
                      transform: "translateX(-50%)",
                      pointerEvents: "none",
                      zIndex: 0,
                    }}
                  >
                    <LottieAnimation
                      path={coins}
                      width="150px"
                      autoplay
                      loop = {false}
                    />
                  </Box>
                )}
              </CardContent>

              {/* CSS Animations */}
              <style>
                {`
                  @keyframes flame {
                    0%, 100% { transform: rotate(-5deg) scale(1); }
                    50% { transform: rotate(5deg) scale(1.1); }
                  }
                  @keyframes celebrate {
                    0%, 100% { transform: rotate(0deg); }
                    25% { transform: rotate(-10deg); }
                    75% { transform: rotate(10deg); }
                  }
                  @keyframes flash {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.3; }
                  }
                `}
              </style>
            </Card>
          </Grow>
        </Container>
      </Backdrop>

      {/* Enhanced Dialogs */}
      <Dialog
        open={rematchDialogOpen}
        onClose={handleRejectRematch}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            background: `linear-gradient(145deg, ${alpha("#000", 0.9)}, ${alpha("#111", 0.95)})`,
            backdropFilter: "blur(20px)",
            border: "2px solid #4ECDC4",
            color: "#fff",
            minWidth: "400px",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            color: "#4ECDC4",
            fontSize: "1.5rem",
            textShadow: "0 0 20px rgba(78, 205, 196, 0.5)",
          }}
        >
          <TrophyIcon sx={{ mr: 1, verticalAlign: "middle", fontSize: "2rem" }} />
          {t("Rematch Request")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ 
            textAlign: "center", 
            py: 3, 
            fontSize: "1.1rem",
            color: "#fff"
          }}>
            {t("Your opponent has requested a rematch. Do you accept?")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 3, pb: 3 }}>
          <Button
            onClick={handleRejectRematch}
            variant="outlined"
            sx={{
              borderRadius: "20px",
              minWidth: "120px",
              height: "50px",
              border: "2px solid #FF4444",
              color: "#FF4444",
              fontSize: "1rem",
              fontWeight: 600,
              "&:hover": {
                background: alpha("#FF4444", 0.1),
                transform: "scale(1.05)",
                boxShadow: "0 0 20px rgba(255, 68, 68, 0.4)",
              },
            }}
          >
            {t("No")}
          </Button>
          <Button
            onClick={handleAcceptRematch}
            variant="contained"
            sx={{
              borderRadius: "20px",
              minWidth: "120px",
              height: "50px",
              background: "linear-gradient(45deg, #4ECDC4, #45B7D1)",
              fontSize: "1rem",
              fontWeight: 600,
              "&:hover": {
                background: "linear-gradient(45deg, #45B7D1, #4ECDC4)",
                transform: "scale(1.05)",
                boxShadow: "0 0 20px rgba(78, 205, 196, 0.6)",
              },
            }}
          >
            {t("Yes")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Response Dialog */}
      <Dialog
        open={responseDialogOpen}
        onClose={() => setResponseDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            background: `linear-gradient(145deg, ${alpha("#000", 0.9)}, ${alpha("#111", 0.95)})`,
            backdropFilter: "blur(20px)",
            border: "2px solid #45B7D1",
            color: "#fff",
            minWidth: "400px",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            color: "#45B7D1",
            fontSize: "1.5rem",
            textShadow: "0 0 20px rgba(69, 183, 209, 0.5)",
          }}
        >
          {t("Information")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ 
            textAlign: "center", 
            py: 3, 
            fontSize: "1.1rem",
            color: "#fff"
          }}>
            {responseMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            onClick={() => setResponseDialogOpen(false)}
            variant="contained"
            sx={{
              borderRadius: "20px",
              minWidth: "120px",
              height: "50px",
              background: "linear-gradient(45deg, #45B7D1, #4ECDC4)",
              fontSize: "1rem",
              fontWeight: 600,
              "&:hover": {
                background: "linear-gradient(45deg, #4ECDC4, #45B7D1)",
                transform: "scale(1.05)",
                boxShadow: "0 0 20px rgba(69, 183, 209, 0.6)",
              },
            }}
          >
            {t("Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GameEndInfo;